import * as UI from '@brewlogix/blx-ui'
import * as CC from '@chakra-ui/react'
import { useToast } from '@chakra-ui/react'
import { AsyncSelect } from 'chakra-react-select'
import classNames from 'classnames'
import React from 'react'
import { useLoginGateStore } from 'src/features/auth/state/useLoginGateStore'
import { userStore } from 'src/features/auth/state/userStore'
import SavedItemToast from 'src/features/favorites/components/SavedItemToast'
import { savedItemToastConfig } from 'src/features/favorites/config/savedItemToastConfig'
import { useFavoriteActionsStore } from 'src/features/favorites/state/useFavoriteActionsStore'
import { BoardItemType, Location } from 'src/types/schema/graphql'
import { asyncSelectConfig } from '../../config/asyncSelectConfig'
import { mapStore } from '../../state/mapStore'
import { useSavedRoutesStore } from '../../state/useSavedRoutesStore'
import { formatOptionLabel } from '../forYou/functions/formatOptionLabel'
import { parseLocations } from '../forYou/functions/parseLocations'
import { parseMapboxPlaces } from '../forYou/functions/parseMapboxPlaces'

const AddRouteStops: React.FC = () => {
    const { user } = userStore()
    const [loading, setLoading] = React.useState(false)

    // Map Store
    const setDirectionsCoordinates = mapStore((state) => state.setDirectionsCoordinates)
    const setShowStopAdd = mapStore((state) => state.setShowStopAdd)
    const suggestedAddresses = mapStore((state) => state.suggestedAddresses)
    const setWildcardText = mapStore((state) => state.setWildcardText)
    const showStopAdd = mapStore((state) => state.showStopAdd)
    const getAddressFromCoords = mapStore((state) => state.getAddressFromCoords)
    const setMobileDirectionsDrawerOpen = mapStore((state) => state.setMobileDirectionsDrawerOpen)
    const directionLegs = mapStore((state) => state.directionLegs)
    const mapView = mapStore((state) => state.mapView)
    const directionCoordinates = mapStore((state) => state.directionCoordinates)
    const defaultCombinedRouteInputData = mapStore((state) => state.defaultCombinedRouteInputData)
    const setDefaultCombinedRouteInputData = mapStore(
        (state) => state.setDefaultCombinedRouteInputData
    )
    const locations = mapStore((state) => state.locations)
    const setActiveFavoriteItem = useFavoriteActionsStore((state) => state.setActiveFavoriteItem)
    const setActiveFavoriteType = useFavoriteActionsStore((state) => state.setActiveFavoriteType)
    const setActiveFavoriteAction = useFavoriteActionsStore(
        (state) => state.setActiveFavoriteAction
    )

    // Saved Routes Store
    const activeSavedRoute = useSavedRoutesStore((state) => state.activeSavedRoute)
    const setTitle = useSavedRoutesStore((state) => state.setTitle)
    const setDescription = useSavedRoutesStore((state) => state.setDescription)
    const createBreweryRoute = useSavedRoutesStore((state) => state.createBreweryRoute)
    const populateRoutes = useSavedRoutesStore((state) => state.populateRoutes)
    const currentRoute = useSavedRoutesStore((state) => state.currentRoute)

    // Login Gate Store
    const setLoginGateActive = useLoginGateStore((state) => state.setLoginGateActive)
    const setUserAction = useLoginGateStore((state) => state.setUserAction)
    interface IAddressOption {
        label: string
        value: string
        coordinates: number[]
        id: string
        location?: Location
    }

    type HandleAddressChangeType = (e: IAddressOption) => Promise<void>

    const handleAddressChange: HandleAddressChangeType = async (e) => {
        await getAddressFromCoords(e.coordinates[1], e.coordinates[0])
            .then((res) => {
                setDirectionsCoordinates({
                    directionCoordinates: {
                        lat: e.coordinates[1],
                        lon: e.coordinates[0],
                        title: e.value,
                        placeName: res,
                        location: e?.location,
                    },
                    action: 'add',
                })
            })
            .catch((err) => console.error('Error getting address from coords', err))
        setShowStopAdd(false)
    }
    const toast = useToast()
    const handleSaveRouteClick = async () => {
        setLoading(true)
        if (user) {
            setActiveFavoriteAction('add')
            if (activeSavedRoute?.title) setTitle(activeSavedRoute.title)
            if (activeSavedRoute?.description) setDescription(activeSavedRoute.description)
            setActiveFavoriteType(BoardItemType.Route)
            const coordinates = activeSavedRoute?.coordinates || directionCoordinates
            const routeId = currentRoute?.uuid
            const distance = activeSavedRoute?.distance || currentRoute?.routes?.[0]?.distance
            const duration = activeSavedRoute?.duration || currentRoute?.routes?.[0]?.duration
            const data = await createBreweryRoute(coordinates, routeId, distance, duration)
            if (data) {
                setActiveFavoriteItem(data)
                toast({
                    ...savedItemToastConfig,
                    render: () => <SavedItemToast item={activeSavedRoute} isRoute={true} />,
                })
                populateRoutes()
            }
        } else {
            setUserAction('Save Route')
            setLoginGateActive(true)
        }
        setLoading(false)
    }
    React.useEffect(() => {
        const initialLocations = parseLocations(locations.slice(0, 5))
        const initialMapboxPlaces = parseMapboxPlaces(suggestedAddresses)
        setDefaultCombinedRouteInputData([...initialLocations, ...initialMapboxPlaces])
    }, [locations])
    const { addStopConfig } = asyncSelectConfig

    return (
        <>
            {showStopAdd && (
                <div className='add-route-stops relative flex px-4 pl-1 lg:-mt-2'>
                    <div className='relative top-6  ml-10 h-3 w-3 min-w-[0.75rem] rounded-full border-[3px] border-primary md:top-[1.7rem]'>
                        <div className='relative top-[7px] hidden h-[40px] w-2 overflow-hidden md:block' />
                    </div>
                    <AsyncSelect
                        {...addStopConfig}
                        onChange={(e: {
                            label: string
                            value: string
                            coordinates: number[]
                            location?: Location
                            id: string
                        }) => handleAddressChange(e)}
                        formatOptionLabel={formatOptionLabel}
                        components={{ DropdownIndicator: () => null }}
                        defaultOptions={defaultCombinedRouteInputData}
                    />
                </div>
            )}
            <div className='mb-2 mt-4 flex pr-[1.15rem] lg:mb-6'>
                <UI.Button
                    theme='link'
                    size='sm'
                    customClasses='w-full flex justify-start pl-9 bg-white border-white py-0 my-2'
                    onClick={() => {
                        setShowStopAdd(true)
                        setWildcardText('')
                    }}
                    iconAlignment='left'
                    icon='HeroIcons.PlusCircle'
                    data-cy='add-a-stop'
                    label='Add a stop'
                    disabled={showStopAdd}
                />
                {!loading ? (
                    <UI.Button
                        size='sm'
                        theme='default'
                        customClasses='w-54 whitespace-nowrap'
                        onClick={handleSaveRouteClick}
                        iconAlignment='left'
                        icon='BeerRoutes'
                        label='Save Route'
                        disabled={directionCoordinates?.length < 2}
                    />
                ) : (
                    <CC.Spinner />
                )}
            </div>
            {directionLegs?.length > 0 && (
                <UI.Button
                    label='View Turn by Turn Directions'
                    onClick={() => setMobileDirectionsDrawerOpen(true)}
                    size='sm'
                    customClasses={classNames(
                        'z-0 flex lg:hidden justify-center border-x-0 rounded-none w-full bg-white border-y-px border-gray-100 py-4',
                        !mapView && 'mb-6'
                    )}
                    icon='SignPost'
                    theme='light'
                    iconAlignment='left'
                />
            )}
        </>
    )
}

export default React.memo(AddRouteStops)
